<script setup lang="ts">
import { computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useValidationHelpers, createLeeftijdRangeValidator } from "@/helpers/validation.helper";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import type { BoekingGroep } from "@/stores/sportactiviteit-bestel.store";

const props = defineProps<{
    item: BoekingGroep;
    index: number;
    canRemove: boolean;
}>();

const emits = defineEmits<{
    (e: "remove"): void;
}>();

const store = useSportactiviteitBestelStore();

const minDeelnemers = computed(() => store.activiteit?.MinDeelnemers ?? 0);
const maxDeelnemers = computed(() => store.activiteit?.MaxDeelnemers ?? 999);
const minBegeleiders = computed(() => store.activiteit?.MinBegeleiders ?? 0);
const maxBegeleiders = computed(() => store.activiteit?.MaxBegeleiders ?? 999);

const groupTitle = computed(() =>
    props.item.OrganisatieNaam.trim() !== ""
        ? props.item.OrganisatieNaam
        : `Groep ${props.index + 1}`,
);

const rules = computed(() => ({
    item: {
        OrganisatieNaam: {
            required: helpers.withMessage("Dit veld is verplicht", required),
        },
        GroepMinLeeftijd: {
            required: helpers.withMessage("Dit veld is verplicht", required),
            min: helpers.withMessage("Leeftijd moet minimaal 1 zijn", minValue(1)),
            leeftijdRange: createLeeftijdRangeValidator(
                () => store.activiteit?.MinLeeftijd ?? 0,
                () => store.activiteit?.MaxLeeftijd ?? 999,
            ),
        },
        GroepMaxLeeftijd: {
            required: helpers.withMessage("Dit veld is verplicht", required),
            min: helpers.withMessage("Leeftijd moet minimaal 1 zijn", minValue(1)),
            leeftijdRange: createLeeftijdRangeValidator(
                () => store.activiteit?.MinLeeftijd ?? 0,
                () => store.activiteit?.MaxLeeftijd ?? 999,
            ),
        },
        Deelnemers: {
            minEen: helpers.withMessage(
                "Vul minimaal 1 deelnemer of begeleider in",
                (val: number) => val > 0 || props.item.Begeleiders > 0,
            ),
        },
    },
}));

const v$ = useVuelidate(rules, props);
const { getFieldErrors } = useValidationHelpers(v$);
</script>

<template>
    <div class="participants-form-group mb-3">
        <div class="participants-form-group-header d-flex align-items-center justify-content-between mb-2">
            <h2 class="payment-title h5 mb-0">{{ groupTitle }}</h2>
            <button
                v-if="canRemove"
                type="button"
                class="btn btn-link p-0 text-danger"
                @click="emits('remove')"
                :aria-label="`Verwijder ${groupTitle}`"
            >
                <span class="icon-delete" aria-hidden="true"></span>
            </button>
        </div>

        <div class="participants-form-input" :class="{ 'has-error': v$.item.Deelnemers.$error }">
            <p class="participants-form-input-text">Deelnemers</p>
            <div class="participants-form-input-controls">
                <button
                    type="button"
                    class="btn-minus participants-form-input-controls-buttons"
                    :disabled="item.Deelnemers <= minDeelnemers"
                    @click="item.Deelnemers = Math.max(minDeelnemers, item.Deelnemers - 1); v$.item.Deelnemers.$touch()"
                    aria-label="Minder deelnemers"
                >
                    <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                </button>
                <input
                    type="number"
                    class="participants-form-input-controls-amount participants-form-input-controls-amount--inline"
                    :id="`groep-${index}-Deelnemers`"
                    :min="minDeelnemers"
                    :max="maxDeelnemers > 0 ? maxDeelnemers : undefined"
                    v-model.number="item.Deelnemers"
                    @blur="v$.item.Deelnemers.$touch()"
                    @change="item.Deelnemers = Math.min(maxDeelnemers || item.Deelnemers, Math.max(minDeelnemers, item.Deelnemers || 0))"
                />
                <button
                    type="button"
                    class="btn-plus participants-form-input-controls-buttons"
                    :disabled="maxDeelnemers > 0 && item.Deelnemers >= maxDeelnemers"
                    @click="item.Deelnemers = Math.min(maxDeelnemers || item.Deelnemers + 1, item.Deelnemers + 1); v$.item.Deelnemers.$touch()"
                    aria-label="Meer deelnemers"
                >
                    <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                </button>
            </div>
        </div>
        <div v-if="v$.item.Deelnemers.$dirty" class="mt-1 mb-2">
            <small v-for="error in v$.item.Deelnemers.$errors" :key="error.$uid" class="text-danger d-block">
                {{ error.$message }}
            </small>
        </div>

        <!-- Begeleiders counter -->
        <div class="participants-form-input">
            <p class="participants-form-input-text">Begeleiders</p>
            <div class="participants-form-input-controls">
                <button
                    type="button"
                    class="btn-minus participants-form-input-controls-buttons"
                    :disabled="item.Begeleiders <= minBegeleiders"
                    @click="item.Begeleiders = Math.max(minBegeleiders, item.Begeleiders - 1); v$.item.Deelnemers.$touch()"
                    aria-label="Minder begeleiders"
                >
                    <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                </button>
                <input
                    type="number"
                    class="participants-form-input-controls-amount participants-form-input-controls-amount--inline"
                    :id="`groep-${index}-Begeleiders`"
                    :min="minBegeleiders"
                    :max="maxBegeleiders > 0 ? maxBegeleiders : undefined"
                    v-model.number="item.Begeleiders"
                    @blur="v$.item.Deelnemers.$touch()"
                    @change="item.Begeleiders = Math.min(maxBegeleiders || item.Begeleiders, Math.max(minBegeleiders, item.Begeleiders || 0))"
                />
                <button
                    type="button"
                    class="btn-plus participants-form-input-controls-buttons"
                    :disabled="maxBegeleiders > 0 && item.Begeleiders >= maxBegeleiders"
                    @click="item.Begeleiders = Math.min(maxBegeleiders || item.Begeleiders + 1, item.Begeleiders + 1); v$.item.Deelnemers.$touch()"
                    aria-label="Meer begeleiders"
                >
                    <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                </button>
            </div>
        </div>

        <div class="row mt-2">
            <div class="col-12" :class="{ 'has-error': v$.item.OrganisatieNaam.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="text"
                        class="form-label-group-input"
                        :id="`groep-${index}-OrganisatieNaam`"
                        :name="`groep-${index}-OrganisatieNaam`"
                        v-model="item.OrganisatieNaam"
                        placeholder="Naam organisatie / groep"
                        @blur="v$.item.OrganisatieNaam.$touch()"
                    />
                    <label :for="`groep-${index}-OrganisatieNaam`">Naam organisatie / groep</label>
                    <div v-for="err in getFieldErrors('item.OrganisatieNaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <div class="col-sm-6" :class="{ 'has-error': v$.item.GroepMinLeeftijd.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="number"
                        class="form-label-group-input"
                        :id="`groep-${index}-GroepMinLeeftijd`"
                        :name="`groep-${index}-GroepMinLeeftijd`"
                        v-model.number="item.GroepMinLeeftijd"
                        min="1"
                        placeholder="Minimumleeftijd"
                        @blur="v$.item.GroepMinLeeftijd.$touch()"
                    />
                    <label :for="`groep-${index}-GroepMinLeeftijd`">Minimumleeftijd</label>
                    <div v-for="err in getFieldErrors('item.GroepMinLeeftijd')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <div class="col-sm-6" :class="{ 'has-error': v$.item.GroepMaxLeeftijd.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="number"
                        class="form-label-group-input"
                        :id="`groep-${index}-GroepMaxLeeftijd`"
                        :name="`groep-${index}-GroepMaxLeeftijd`"
                        v-model.number="item.GroepMaxLeeftijd"
                        min="1"
                        placeholder="Maximumleeftijd"
                        @blur="v$.item.GroepMaxLeeftijd.$touch()"
                    />
                    <label :for="`groep-${index}-GroepMaxLeeftijd`">Maximumleeftijd</label>
                    <div v-for="err in getFieldErrors('item.GroepMaxLeeftijd')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>